import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { Table, Empty, Col, Row, message, Tooltip, Breadcrumb } from "antd";
import {
  RollbackOutlined,
  MenuUnfoldOutlined,
  BarsOutlined,
  CheckOutlined,
  BankOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ColumnHelper, RowHelper } from "util/TableHelper";
import { odata, queryHelper } from "../Filters/Helper";
import RequestHandler from "../../util/RequestHandler";
import AccountPageTableFilter from "../Filters/AccountPageTableFilter";
import config from "../../util/ApiforContext";

const AccountPage = (props) => {
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState(
    queryHelper.getDefaultTableDataQueryOptions()
  );
  const history = useHistory();

  useEffect(() => {
    setFilterValues();
    getTableData();
  }, [tableDataQueryOptions]);

  const getFilterQuery = () => {
    const { filter } = tableDataQueryOptions;

    const conditions = [];

    const [bankId, currencyCode] = props.match.params.tenantid.split("-");
    if (bankId) {
      conditions.push(`bankId eq ${bankId}`);
    }

    if (currencyCode) {
      conditions.push(`currency eq '${currencyCode}'`);
    }

    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length !== 0) {
      return "$filter=" + conditions.join(" and ");
    }
    return "";
  };

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });
    const { skip, take, order } = tableDataQueryOptions;

    RequestHandler.get(
      `/TenantAccount?$count=true&${getFilterQuery()}${order}&$skip=${skip}&$top=${take}`
    ).then((response) => {
      if (Array.isArray(response.value)) {
        setTableData({
          data: response.value,
          total: response["@odata.count"],
          loading: false,
        });
      }
    });
  };

  const setFilterValues = () => {
    const filterValues = {
      ...tableDataQueryOptions.values,
      skip: tableDataQueryOptions.skip,
      take: tableDataQueryOptions.take,
      current: tableDataQueryOptions.current,
      order: tableDataQueryOptions.order,
      filter: tableDataQueryOptions.filter,
    };
    const queryString = queryHelper.getQueryFromValues(filterValues);

    history.replace({
      pathname: window.location.pathname,
      search: queryString,
    });
  };

  const paging = (p, _, sorter) => {
    let orderValue = "";
    if (sorter.columnKey && sorter.order) {
      orderValue =
        "&" +
        (sorter.order === "ascend"
          ? odata.orderBy(sorter.columnKey)
          : odata.orderByDesc(sorter.columnKey));
    }

    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
        order: orderValue,
      };
    });
  };

  const onFinishTableFilter = (query, values) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: 0,
        current: 1,
        filter: query,
        values: values,
      };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      ...tableDataQueryOptions,
      skip: 0,
      current: 1,
      filter: "",
      values: {},
    });
  };

  const onClickExportExcel = () => {
    const hideMessage = message.loading("Excel indiriliyor...", 0);

    RequestHandler.post(
      `/TenantAccount/ExportExcel`,
      {
        query: getFilterQuery(),
      },
      false
    ).then((response) => {
      hideMessage();

      if (response) {
        RequestHandler.post(
          "/TenantAccount/DownloadExcel",
          { fileName: response },
          false,
          {
            responseType: "blob",
          }
        ).then((blobResponse) => {
          const url = window.URL.createObjectURL(new Blob([blobResponse]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Banka_Hesapları.xlsx");
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        });
      }
    });
  };

  const columns = [
    {
      title: "Banka",
      dataIndex: "bankLogo",
      key: "bankName",
      render: (data) => {
        return (
          <div style={{ width: 100 }}>
            {" "}
            <img src={data}></img>
          </div>
        );
      },
    },
    {
      title: "Şube Adı / Kodu",
      dataIndex: "branchName",
      align: "center",
      key: "branchName",
      width: "15%",
      render: (text, row) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            {row.branchName == null
              ? row.branchCode == null
                ? ""
                : row.branchCode
              : row.branchName}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Hesap Adı",
      dataIndex: "name",
      align: "center",
      key: "name",
      width: "10%",
    },
    {
      title: "Iban / Hesap Numarası",
      dataIndex: "iban",
      align: "center",
      key: "iban",
      width: "15%",
      render: (_, row) => (row.iban ? row.iban : row.accountNumber),
    },
    {
      title: "Döviz",
      dataIndex: "currency",
      align: "center",
      key: "currency",
      width: "10%",
    },
    {
      title: "Bakiye",
      dataIndex: "balanceString",
      key: "balance",
      sorter: true,
      align: "center",
      render: (data) => <div style={{ textAlign: "right" }}>{data}</div>,
    },
    {
      title: "Tip",
      dataIndex: "type",
      align: "center",
      key: "type",
      width: "10%",
      render: (_, row) => {
        if (row.typeValue != null) {
          return <div>{row.type}</div>;
        }
      },
    },
    {
      title: "Son Sorgulanma Tarihi",
      dataIndex: "lastQueryDate",
      key: "lastQueryDate",
      sorter: true,
      align: "center",
      render: (data) =>
        data ? (
          <div>
            {moment(data).format("L")} {moment(data).format("LT")}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Detay",
      dataIndex: "",
      width: "2%",
      align: "center",
      render: (record) => {
        return (
          <Link
            style={{ fontSize: "150%" }}
            to={`/bank-accounts-account-transaction/${record.id}`}
          >
            <MenuUnfoldOutlined />
          </Link>
        );
      },
    },
    {
      title: "Hesaplamaya Dahil Et",
      dataIndex: "isCalculated",
      width: "2%",
      align: "center",
      render: (data) => (
        <div style={{ color: data === true ? "green" : "red" }}>
          <CheckOutlined />
        </div>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "Kullanılabilir Bakiye",
      dataIndex: "availableBalance",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: "Kredi Limiti",
      dataIndex: "creditLimit",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: "Kredi Bakiyesi",
      dataIndex: "creditBalance",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: "Bloke Tutarı",
      dataIndex: "blockedAmount",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: "Vade Bitiş Tarihi",
      dataIndex: "dueDate",
      render: (data) => (data ? moment(data).format("DD.MM.YYYY") : "-"),
    },
    {
      title: "Vade Faiz Oranı",
      dataIndex: "interestRate",
    },
    {
      title: "Hesap Açılış Tarihi",
      dataIndex: "openDate",
      render: (data) => (data ? moment(data).format("DD.MM.YYYY") : "-"),
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: 9 }}>
        <Col xs={8}>
          <a onClick={() => history.goBack()} style={{ color: "black" }}>
            {" "}
            <RollbackOutlined /> GERİ DÖN
          </a>
        </Col>
        <Col xs={16}>
          <Row justify="end" align="middle">
            <Breadcrumb>
              <Breadcrumb.Item href="/bank-list">
                Banka Bakiyeleri
              </Breadcrumb.Item>
              <Breadcrumb.Item>Banka Hesapları</Breadcrumb.Item>
            </Breadcrumb>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col xs={24} md={12} lg={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <BankOutlined /> BANKA HESAPLARI
          </p>
        </Col>
        <Col xs={24} md={12} lg={8} style={{ textAlign: "right" }}>
          <Tooltip title="Excel İndir">
            <a
              style={{
                color: "green",
                fontSize: "180%",
                marginRight: 30,
              }}
              onClick={onClickExportExcel}
            >
              <FileExcelOutlined />
            </a>
          </Tooltip>
        </Col>
      </Row>

      <AccountPageTableFilter
        data={tableDataQueryOptions.values}
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            expandable={{
              expandedRowRender: (row) =>
                RowHelper.renderRowDetail({
                  data: row,
                  columns: detailColumns,
                }),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%", width: "100%" }}
            rowKey="id"
            filterable={true}
            sortable={true}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
            size="small"
          />
        </Col>
      </Row>
    </>
  );
};

export default AccountPage;
